import type { Coordinate } from './types.ts';

const M_PER_DEG = 111320;
export const SPACING_M = 4;
/** Meters per SVG unit of the 8x8 chevron art. */
const SCALE_M = 0.3;

/** Chevron outline from the design SVG (8x8, y down), pointing right. */
const SHAPE: [number, number][] = [
  [0, 2.40186],
  [3.36228, 0],
  [7.60531, 1.20073],
  [7.36531, 5.60384],
  [4.00302, 8.00566],
  [4.52322, 3.4024],
  [0, 2.40186],
];
const CENTER: [number, number] = [4, 4];
/** The art's symmetry axis (notch to tip) is not horizontal; this is its angle (y up). */
const TIP_ANGLE = Math.atan2(3.4024 - 1.20073, 7.60531 - 4.52322);
const COS = Math.cos(TIP_ANGLE);
const SIN = Math.sin(TIP_ANGLE);

const LATERAL = SHAPE.map(([sx, sy]) => -(sx - CENTER[0]) * SIN - (sy - CENTER[1]) * COS);
/** Chevron width across the route, as a fraction of chevron spacing (both scale together). */
export const CHEVRON_WIDTH_RATIO = ((Math.max(...LATERAL) - Math.min(...LATERAL)) * SCALE_M) / SPACING_M;

/** Index of the inward notch vertex in SHAPE. */
const NOTCH_INDEX = 5;

export type Chevron = {
  ring: Coordinate[];
  notch: [number, number];
  /** Shared endpoints tuple identifying the source line segment. */
  segment: [Coordinate, Coordinate];
  /** Point on the route line the chevron is placed at. */
  center: [number, number];
  /** Unit route direction in meters, [east, north]. */
  dir: [number, number];
};

/** Vector in meters, [east, north], from one [lng, lat] to another. */
export const vectorM = ([lng1, lat1]: [number, number], [lng2, lat2]: [number, number]): [number, number] => [
  (lng2 - lng1) * M_PER_DEG * Math.cos((lat1 * Math.PI) / 180),
  (lat2 - lat1) * M_PER_DEG,
];

/**
 * Builds one chevron polygon ring, in lng/lat.
 * @param center - [lng, lat] the chevron is centred on.
 * @param dir - Unit direction in meters, [east, north], the tip points along.
 * @param scale - Multiplier applied to the chevron size.
 */
export const buildChevronRing = ([lng, lat]: [number, number], [ux, uy]: [number, number], scale = 1): Coordinate[] => {
  const scaleM = SCALE_M * scale;
  const kx = M_PER_DEG * Math.cos((lat * Math.PI) / 180);
  return SHAPE.map(([sx, sy]) => {
    // Local frame: +x along the route, +y to its left. SVG y points down, so flip it.
    const ax = (sx - CENTER[0]) * scaleM;
    const ay = -(sy - CENTER[1]) * scaleM;
    // Rotate by -TIP_ANGLE so the tip points along +x.
    const lx = ax * COS + ay * SIN;
    const ly = -ax * SIN + ay * COS;
    const x = lx * ux - ly * uy;
    const y = lx * uy + ly * ux;
    return [lng + x / kx, lat + y / M_PER_DEG] as unknown as Coordinate;
  });
};

/**
 * Builds filled chevron polygons, in lng/lat, spaced evenly along a route line.
 * The chevrons are real geometry, so MapLibre scales and clips them with the map.
 * @param coordinates - Route line coordinates as [lng, lat].
 * @param scale - Multiplier applied to both chevron size and spacing.
 * @returns One chevron (closed polygon ring plus placement) per step along the line, in route order.
 */
export const buildChevrons = (coordinates: Coordinate[], scale = 1): Chevron[] => {
  const chevrons: Chevron[] = [];
  const spacing = SPACING_M * scale;
  let carried = spacing / 2;

  for (let i = 0; i < coordinates.length - 1; i++) {
    const [lng1, lat1] = coordinates[i] as number[];
    const [lng2, lat2] = coordinates[i + 1] as number[];
    const kx = M_PER_DEG * Math.cos((lat1! * Math.PI) / 180);
    const dx = (lng2! - lng1!) * kx;
    const dy = (lat2! - lat1!) * M_PER_DEG;
    const len = Math.hypot(dx, dy);
    if (len === 0) {
      continue;
    }
    const ux = dx / len;
    const uy = dy / len;
    const segment: Chevron['segment'] = [coordinates[i]!, coordinates[i + 1]!];

    let d = carried;
    for (; d <= len; d += spacing) {
      const center: [number, number] = [lng1! + (ux * d) / kx, lat1! + (uy * d) / M_PER_DEG];
      const ring = buildChevronRing(center, [ux, uy], scale);
      chevrons.push({ ring, notch: ring[NOTCH_INDEX] as unknown as [number, number], segment, center, dir: [ux, uy] });
    }
    // Distance from the end of this segment to the next chevron.
    carried = d - len;
  }
  return chevrons;
};
